/**
 * Fase 5 — Kurs-veiviser med 5 steg (grunninfo, tidsplan, lokasjon, sensor, publisering).
 *
 * Fase 6 vil erstatte LocationStep med Leaflet-kart.
 * Fase 9 kan utvide beskrivelses-feltet med KI-utleidet forslag.
 */
import { useEffect, useState } from 'react';
import { BookOpen, Save, Loader2, Rocket } from 'lucide-react';
import WizardModal, { type WizardStepDef, type WizardFinalAction } from './WizardModal';
import { authenticatedFetch } from '../../lib/auth';
import { useToast } from '../ui';
import CourseBasicsStep from './course-steps/BasicsStep';
import CourseScheduleStep from './course-steps/ScheduleStep';
import CourseLocationStep from './course-steps/LocationStep';
import SensorStep from './course-steps/SensorStep';
import CoursePublishStep from './course-steps/PublishStep';

export interface SelectedSensor {
  id: string;
  full_name: string;
  email: string;
  same_org: boolean;
  habilitet_reason?: string;
}

/** CZ: per-komponent deadline fra prøvesett-template. */
export interface CourseComponentDeadline {
  component_type: string;
  /** ISO-datetime-local (f.eks. "2026-11-30T23:59") */
  deadline: string;
  /** Hjelpetekst fra template (task_description) */
  task_description?: string | null;
}

export interface CourseWizardData {
  // Steg 1
  exam_set_id: string;
  member_id: string;
  name: string;
  description: string;

  // Steg 2
  start_date: string;
  end_date: string;
  max_candidates: number | null;
  exam_date: string;
  exam_time: string;
  /** CZ: Per-komponent-datoer, fylles ut når prøvesettet er kompositt. */
  component_deadlines: CourseComponentDeadline[];

  // Steg 3
  location_type: 'physical' | 'digital' | 'blended';
  location: string;
  city: string;
  location_lat: number | null;
  location_lng: number | null;
  meeting_url: string;
  meeting_instructions: string;

  // Steg 4
  selected_sensors: SelectedSensor[];

  // Steg 5
  visible_on_kompetanseheving: boolean;
  categories: string[];
  show_registered_count: boolean;
  registration_fee_nok: number;
  self_cancel_deadline_days: number;
  no_show_fee_active: boolean;
}

const INITIAL_DATA: CourseWizardData = {
  exam_set_id: '',
  member_id: '',
  name: '',
  description: '',

  start_date: '',
  end_date: '',
  max_candidates: null,
  exam_date: '',
  exam_time: '',
  component_deadlines: [],

  location_type: 'physical',
  location: '',
  city: '',
  location_lat: null,
  location_lng: null,
  meeting_url: '',
  meeting_instructions: '',

  selected_sensors: [],

  visible_on_kompetanseheving: true,
  categories: [],
  show_registered_count: false,
  registration_fee_nok: 0,
  self_cancel_deadline_days: 30,
  no_show_fee_active: false,
};

export interface NewCourseInstanceWizardProps {
  open: boolean;
  onClose: () => void;
  /** Hvis satt: hopp over prøvesett-velger (kommer fra forrige veiviser) */
  preselectedExamSetId?: string;
  onComplete?: (courseInstanceId: string) => void;
}

export default function NewCourseInstanceWizard({
  open,
  onClose,
  preselectedExamSetId,
  onComplete,
}: NewCourseInstanceWizardProps) {
  const toast = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState<CourseWizardData>(INITIAL_DATA);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [examDurationMinutes, setExamDurationMinutes] = useState<number | undefined>();
  const [examSetTitle, setExamSetTitle] = useState<string>('');

  const patch = (updates: Partial<CourseWizardData>) => {
    setData((prev) => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const reset = () => {
    setCurrentStep(0);
    setData(INITIAL_DATA);
    setHasChanges(false);
    setSaving(false);
    setExamDurationMinutes(undefined);
    setExamSetTitle('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // Preselect exam_set hvis gitt
  useEffect(() => {
    if (preselectedExamSetId && !data.exam_set_id) {
      patch({ exam_set_id: preselectedExamSetId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedExamSetId]);

  // Hent exam-set-metadata (duration + title + CZ composite_template) når ID endrer seg
  useEffect(() => {
    if (!data.exam_set_id) return;
    (async () => {
      try {
        const res = await authenticatedFetch(`/api/exams/${data.exam_set_id}`);
        const json = await res.json();
        const examSet = json.exam_set || json;
        if (examSet) {
          setExamDurationMinutes(examSet.time_limit_minutes || undefined);
          setExamSetTitle(examSet.title || '');
          // CZ: Hvis prøvesettet er kompositt, forhåndsutfyll component_deadlines
          // basert på templaten (uten dato — settes av bruker i ScheduleStep).
          if (examSet.composite_mode && Array.isArray(examSet.composite_template)) {
            const initDeadlines = examSet.composite_template.map((c: any) => ({
              component_type: c.component_type,
              deadline: '', // bruker fyller inn i ScheduleStep (default = end_date)
              task_description: c.task_description || null,
            }));
            patch({ component_deadlines: initDeadlines });
          } else {
            // Single-component: lag én innslag for primary_assessment_type så ScheduleStep
            // også kan tilby frist for ikke-skriftlige single-prøvesett.
            const primaryType = examSet.primary_assessment_type || 'written';
            patch({ component_deadlines: [{ component_type: primaryType, deadline: '', task_description: null }] });
          }
        }
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.exam_set_id]);

  const validate = (): string | null => {
    if (!data.exam_set_id) return 'Prøvesett må velges (steg 1).';
    if (!data.member_id) return 'Medlemsorganisasjon må velges (steg 1).';
    if (!data.name.trim()) return 'Kursnavn må fylles ut (steg 1).';
    if (!data.start_date) return 'Kursstart-dato må fylles ut (steg 2).';
    if (data.location_type !== 'digital' && !data.location.trim()) {
      return 'Adresse må fylles ut for fysisk/blandet kurs (steg 3).';
    }
    if (data.location_type !== 'physical' && !data.meeting_url.trim()) {
      return 'Møte-URL må fylles ut for digitalt/blandet kurs (steg 3).';
    }
    const needsReason = data.selected_sensors.some(
      (s) => s.same_org && (s.habilitet_reason || '').trim().length < 20,
    );
    if (needsReason) return 'Habilitet-begrunnelse (min 20 tegn) må fylles ut for sensorer i samme organisasjon (steg 4).';
    return null;
  };

  const handleSave = async (): Promise<string | null> => {
    const err = validate();
    if (err) {
      toast.showWarning(err);
      return null;
    }
    setSaving(true);
    try {
      // Beregn exam_window_end
      let exam_window_start: string | undefined;
      let exam_window_end: string | undefined;
      if (data.exam_date && data.exam_time) {
        const start = new Date(`${data.exam_date}T${data.exam_time}:00`);
        exam_window_start = start.toISOString();
        if (examDurationMinutes) {
          exam_window_end = new Date(start.getTime() + examDurationMinutes * 60_000).toISOString();
        }
      }

      // 1. Opprett kurs
      const createRes = await authenticatedFetch('/api/admin/course-instances', {
        method: 'POST',
        body: JSON.stringify({
          name: data.name.trim(),
          exam_set_id: data.exam_set_id,
          member_id: data.member_id,
          start_date: data.start_date,
          end_date: data.end_date || undefined,
          max_candidates: data.max_candidates ?? undefined,
          status: 'active',
          exam_window_start,
          exam_window_end,
          location: data.location.trim() || undefined,
          location_lat: data.location_lat ?? undefined,
          location_lng: data.location_lng ?? undefined,
          description: data.description.trim() || undefined,
          visible_on_kompetanseheving: data.visible_on_kompetanseheving,
          registration_fee_nok: data.registration_fee_nok,
          self_cancel_deadline_days: data.self_cancel_deadline_days,
          no_show_fee_active: data.no_show_fee_active,
          show_registered_count: data.show_registered_count,
          location_type: data.location_type,
          city: data.city.trim() || undefined,
          meeting_url: data.meeting_url.trim() || undefined,
          meeting_instructions: data.meeting_instructions.trim() || undefined,
          categories: data.categories,
          approval_mode: 'auto',
          // CZ: send per-komponent-frister. Backend bruker end_date som fallback
          // hvis en komponents deadline er tom.
          component_deadlines: data.component_deadlines
            .filter(d => d.component_type && d.deadline)
            .map(d => ({
              component_type: d.component_type,
              deadline: new Date(d.deadline).toISOString(),
            })),
        }),
      });
      const createJson = await createRes.json();
      if (!createRes.ok || !createJson.success) {
        throw new Error(createJson.error || createJson.message || 'Kunne ikke opprette kurs');
      }
      // Backend returnerer `instance_id` (admin-routes.js:2748). Behold fallbacks for
      // bakoverkompatibilitet med evt. eldre deploy-varianter.
      const courseId: string =
        createJson.instance_id ||
        createJson.course_instance?.id ||
        createJson.course_instance_id ||
        createJson.id;
      if (!courseId) {
        throw new Error('Backend returnerte ikke course_instance_id — kan ikke tildele sensorer.');
      }

      // 2. Tildel sensorer (sekvensielt for å fange habilitet-409)
      let sensorErrors: string[] = [];
      for (const sensor of data.selected_sensors) {
        try {
          const assignRes = await authenticatedFetch('/api/admin/assign-sensor-to-course', {
            method: 'POST',
            headers: sensor.same_org
              ? { 'Content-Type': 'application/json', 'X-Confirm-Habilitet': 'true' }
              : undefined,
            body: JSON.stringify({
              sensor_user_id: sensor.id,
              course_instance_id: courseId,
              habilitet_reason: sensor.same_org ? (sensor.habilitet_reason || '').trim() : undefined,
            }),
          });
          if (!assignRes.ok) {
            const errJson = await assignRes.json().catch(() => ({}));
            sensorErrors.push(`${sensor.full_name}: ${errJson.error || errJson.message || `HTTP ${assignRes.status}`}`);
          }
        } catch (err_: unknown) {
          sensorErrors.push(`${sensor.full_name}: ${err_ instanceof Error ? err_.message : 'ukjent feil'}`);
        }
      }

      if (sensorErrors.length > 0) {
        toast.showWarning(`Kurs opprettet, men noen sensor-tildelinger feilet: ${sensorErrors.join('; ')}`);
      } else {
        toast.showSuccess('Kurs opprettet og sensorer tildelt!');
      }
      return courseId;
    } catch (err_: unknown) {
      const msg = err_ instanceof Error ? err_.message : 'Ukjent feil';
      toast.showError('Lagring feilet: ' + msg);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClose = async () => {
    const id = await handleSave();
    if (id) {
      onComplete?.(id);
      reset();
      onClose();
    }
  };

  if (!open) return null;

  // ===== Steg-definisjoner =====
  const steps: WizardStepDef[] = [
    {
      key: 'basics',
      label: 'Grunninfo',
      description: preselectedExamSetId ? 'Tittel og beskrivelse' : 'Prøvesett, tittel og beskrivelse',
      content: (
        <CourseBasicsStep
          data={data}
          patch={patch}
          skipExamSetPicker={Boolean(preselectedExamSetId)}
        />
      ),
      canAdvance:
        !data.exam_set_id
          ? 'Prøvesett må velges.'
          : !data.member_id
          ? 'Medlemsorganisasjon må velges.'
          : !data.name.trim()
          ? 'Kursnavn må fylles ut.'
          : true,
    },
    {
      key: 'schedule',
      label: 'Tidsplan',
      description: 'Datoer, plasser og eksamenstidspunkt',
      content: <CourseScheduleStep data={data} patch={patch} examDurationMinutes={examDurationMinutes} />,
      canAdvance: data.start_date ? true : 'Kursstart-dato må fylles ut.',
    },
    {
      key: 'location',
      label: 'Lokasjon',
      description: 'Fysisk sted eller digital lenke',
      content: <CourseLocationStep data={data} patch={patch} />,
      canAdvance:
        data.location_type !== 'digital' && !data.location.trim()
          ? 'Adresse må fylles ut.'
          : data.location_type !== 'physical' && !data.meeting_url.trim()
          ? 'Møte-URL må fylles ut.'
          : true,
    },
    {
      key: 'sensor',
      label: 'Sensor',
      description: 'Tildel sensorer til kurset',
      content: <SensorStep data={data} patch={patch} />,
      canAdvance: (() => {
        const bad = data.selected_sensors.find((s) => s.same_org && (s.habilitet_reason || '').trim().length < 20);
        return bad ? `Habilitet-begrunnelse mangler for ${bad.full_name} (min 20 tegn).` : true;
      })(),
    },
    {
      key: 'publish',
      label: 'Publisering',
      description: 'Oppsummering og publiseringsvalg',
      content: <CoursePublishStep data={data} patch={patch} examSetTitle={examSetTitle} />,
    },
  ];

  const canSave = !validate();
  const finalActions: WizardFinalAction[] = [
    {
      key: 'save',
      label: 'Opprett kurs',
      variant: 'success',
      icon: saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />,
      onClick: handleSaveClose,
      loading: saving,
      disabled: !canSave || saving,
      disabledReason: validate() ?? undefined,
    },
  ];

  return (
    <WizardModal
      open={open}
      onClose={handleClose}
      title="Ny kursgjennomføring"
      subtitle={
        preselectedExamSetId
          ? 'Prøvesett valgt fra forrige veiviser'
          : 'Opprett et konkret kurs knyttet til et eksisterende prøvesett'
      }
      icon={preselectedExamSetId ? <Rocket className="w-4 h-4 text-kf-orange-500" /> : <BookOpen className="w-4 h-4 text-kf-orange-500" />}
      steps={steps}
      currentStep={currentStep}
      onStepChange={setCurrentStep}
      finalActions={finalActions}
      hasChanges={hasChanges}
    />
  );
}
